import React from "react";
import { Zap, MousePointer2, CircleDot } from "lucide-react";
import type {
  NodeKind,
  PrimitiveType,
  ActionSubtype,
  IdeaNodeData,
  PortDirection,
  InfoPort,
  DbColumn,
  DbTable,
} from "./types";

// ─── Shared Style Constants ────────────────────────────────────────────────────

export const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-black outline-none focus:border-gray-400 dark:border-white/10 dark:bg-zinc-900 dark:text-white";
export const labelCls =
  "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-400";

// ─── Node Metadata ─────────────────────────────────────────────────────────────

export const NODE_META: Record<
  NodeKind,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    bg: string;
    border: string;
    ring: string;
    handleColor: string;
  }
> = {
  signal: {
    label: "Signal",
    icon: MousePointer2,
    color: "text-sky-500",
    bg: "bg-sky-500/10",
    border: "border-sky-500/30",
    ring: "ring-sky-400/50",
    handleColor: "#0ea5e9",
  },
  state: {
    label: "Zustand",
    icon: CircleDot,
    color: "text-violet-500",
    bg: "bg-violet-500/10",
    border: "border-violet-500/30",
    ring: "ring-violet-400/50",
    handleColor: "#8b5cf6",
  },
  action: {
    label: "Action",
    icon: Zap,
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    ring: "ring-amber-400/50",
    handleColor: "#fbbf24",
  },
};

// ─── Lookup Tables ─────────────────────────────────────────────────────────────

export const PRIMITIVE_TYPES: PrimitiveType[] = [
  "string",
  "number",
  "boolean",
  "date",
  "uuid",
  "json",
];

export const ACTION_SUBTYPES: { value: ActionSubtype; label: string }[] = [
  { value: "local_function", label: "Funktion" },
  { value: "external_api", label: "Externe API" },
  { value: "llm_prompt", label: "LLM Prompt" },
];

// ─── Special Handle IDs ───────────────────────────────────────────────────────

/** Fixed handle ID for the execution-gate input. */
export const EXECUTION_GATE_HANDLE_ID = "__execution_gate__";

// ─── Helpers ───────────────────────────────────────────────────────────────────

export const uid = () => Math.random().toString(36).slice(2, 10);

export function defaultPayload(kind: NodeKind): Partial<IdeaNodeData> {
  switch (kind) {
    case "signal":
      return { signal: { event: "submit", source: "Formular", payload: "" } };
    case "state":
      return { state: { value: "idle", enterWhen: "", exitWhen: "" } };
    case "action":
      return { actionPayload: { subtype: "local_function" } };
  }
}

export function emptyPort(
  direction: PortDirection = "input",
  name = "",
): InfoPort {
  return {
    id: uid(),
    name,
    format: "primitive",
    primitiveType: "string",
    required: true,
    direction,
    description: "",
    notes: [],
  };
}

export function emptyDbColumn(): DbColumn {
  return { id: uid(), name: "", type: "string", nullable: false };
}

export function emptyDbTable(): DbTable {
  return { id: uid(), name: "", columns: [] };
}

export function emptyNodeData(kind: NodeKind): IdeaNodeData {
  return {
    nodeKind: kind,
    title: "",
    description: "",
    notes: [],
    ports: [],
    ...defaultPayload(kind),
  };
}
